(() => {
  if (window.SixteaShopifyBookingV2Loaded) return;
  window.SixteaShopifyBookingV2Loaded = true;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const ADD_URL = `${ROOT}cart/add.js`;
  const CLEAR_URL = `${ROOT}cart/clear.js`;
  const CHECKOUT_URL = `${ROOT}checkout`;

  const normalizeKey = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const parseConfigs = () => {
    const ordered = [];
    const map = new Map();

    document.querySelectorAll('[data-sixtea-shopify-config]').forEach((node) => {
      try {
        const config = JSON.parse(node.textContent || '{}');
        (config.experiences || []).forEach((experience) => {
          const key = normalizeKey(experience && experience.key);
          if (!experience || !key) return;
          const normalized = { ...experience, key };
          if (!map.has(key)) ordered.push(normalized);
          map.set(key, normalized);
        });
      } catch (error) {
        console.warn('SIXTEA Shopify booking configuration could not be read.', error);
      }
    });

    return { ordered, map };
  };

  const money = (cents, currency) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  };

  const textValue = (form, selector) => {
    const field = form.querySelector(selector);
    return field ? String(field.value || '').trim() : '';
  };

  const setStatus = (form, message, state = '') => {
    const node = form.querySelector('[data-sixtea-booking-status]');
    if (!node) return;
    node.textContent = message || '';
    node.hidden = !message;
    if (state) node.dataset.state = state;
    else delete node.dataset.state;
  };

  const getAvailableVariants = (experience) =>
    (experience && experience.variants || []).filter((variant) => variant.available);

  const populateExperienceSelect = (select, experiences, requestedKey) => {
    if (!select || select.tagName !== 'SELECT') return;

    const current = normalizeKey(requestedKey || select.value || select.dataset.defaultExperience);
    select.replaceChildren();

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Choose an experience';
    select.appendChild(placeholder);

    experiences.forEach((experience) => {
      const option = document.createElement('option');
      option.value = experience.key;
      const ready = experience.productConfigured && getAvailableVariants(experience).length > 0;
      option.textContent = ready ? experience.name : `${experience.name} — inquiry only`;
      select.appendChild(option);
    });

    if ([...select.options].some((option) => option.value === current)) {
      select.value = current;
    }
  };

  const updatePrice = (form) => {
    const experience = form.__sixteaExperience;
    const priceText = form.querySelector('[data-sixtea-selected-price]');
    const variantSelect = form.querySelector('[data-sixtea-variant-select]');
    const guestInput = form.querySelector('[data-sixtea-guest-count]');

    if (!experience || !priceText || !variantSelect || !variantSelect.value) {
      if (priceText) priceText.textContent = '';
      return;
    }

    const variant = (experience.variants || []).find(
      (item) => String(item.id) === String(variantSelect.value)
    );
    if (!variant) return;

    const guestCount = Math.max(1, Number((guestInput && guestInput.value) || 1));
    const quantity = experience.quantityMode === 'guest_count' ? guestCount : 1;
    const total = Number(variant.price || 0) * quantity;
    const suffix = experience.quantityMode === 'guest_count'
      ? ` · ${quantity} guest${quantity === 1 ? '' : 's'}`
      : '';

    priceText.textContent = `${money(total, experience.currency)}${suffix}`;
  };

  const updateForm = (form, experience) => {
    const variantField = form.querySelector('[data-sixtea-variant-field]');
    const variantSelect = form.querySelector('[data-sixtea-variant-select]');
    const guestInput = form.querySelector('[data-sixtea-guest-count]');
    const experienceText = form.querySelector('[data-sixtea-selected-experience]');
    const priceText = form.querySelector('[data-sixtea-selected-price]');
    const submit = form.querySelector('[data-sixtea-checkout-submit]');

    form.__sixteaExperience = experience || null;
    setStatus(form, '');

    if (experienceText) {
      experienceText.textContent = experience ? experience.name : 'Select an experience';
    }

    if (!experience || !experience.productConfigured) {
      if (variantSelect) {
        variantSelect.replaceChildren();
        variantSelect.disabled = true;
        variantSelect.required = false;
      }
      if (variantField) variantField.hidden = true;
      if (priceText) priceText.textContent = '';
      if (submit) submit.disabled = true;
      if (experience) {
        setStatus(
          form,
          experience.unavailableMessage || 'This experience has not been connected to a Shopify product yet.',
          'error'
        );
      }
      return;
    }

    const variants = getAvailableVariants(experience);
    if (variantSelect) {
      variantSelect.replaceChildren();
      variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.isDefault ? experience.name : variant.title;
        option.dataset.price = String(variant.price);
        variantSelect.appendChild(option);
      });
      variantSelect.disabled = variants.length === 0;
      variantSelect.required = variants.length > 0;
    }

    if (variantField) {
      variantField.hidden = variants.length <= 1 && variants[0] && variants[0].isDefault;
    }

    if (guestInput) {
      guestInput.min = String(Math.max(1, Number(experience.minimumQuantity || 1)));
      if (!guestInput.value || Number(guestInput.value) < Number(guestInput.min)) {
        guestInput.value = guestInput.min;
      }
    }

    if (submit) submit.disabled = variants.length === 0;
    updatePrice(form);

    if (variants.length === 0) {
      setStatus(form, 'This experience is currently unavailable in Shopify.', 'error');
    }
  };

  const chooseExperience = (form, key, map) => {
    if (!form) return false;

    const normalizedKey = normalizeKey(key);
    const select = form.querySelector('[data-sixtea-experience-select]');
    const experience = map.get(normalizedKey) || null;

    if (select && select.tagName === 'SELECT') {
      select.value = experience ? normalizedKey : '';
    }

    updateForm(form, experience);
    return Boolean(experience);
  };

  const property = (properties, label, value) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      properties[label] = String(value).trim();
    }
  };

  const submitCheckout = async (form) => {
    const experience = form.__sixteaExperience;
    const variantSelect = form.querySelector('[data-sixtea-variant-select]');
    const submit = form.querySelector('[data-sixtea-checkout-submit]');

    if (!form.reportValidity()) return;

    if (!experience || !experience.productConfigured || !variantSelect || !variantSelect.value) {
      setStatus(form, 'Choose an available experience and package before continuing.', 'error');
      return;
    }

    const guestCount = Math.max(1, Number(textValue(form, '[data-sixtea-guest-count]') || 1));
    const minimum = Math.max(1, Number(experience.minimumQuantity || 1));

    if (guestCount < minimum) {
      setStatus(form, `This experience requires a minimum guest count of ${minimum}.`, 'error');
      return;
    }

    const variant = (experience.variants || []).find(
      (item) => String(item.id) === String(variantSelect.value)
    );

    if (!variant || !variant.available) {
      setStatus(form, 'The selected package is no longer available. Choose another option.', 'error');
      return;
    }

    const properties = {};
    property(properties, 'Experience', experience.name);
    property(properties, 'Package', variant.isDefault ? '' : variant.title);
    property(properties, 'Booking name', textValue(form, '[data-sixtea-name]'));
    property(properties, 'Booking email', textValue(form, '[data-sixtea-email]'));
    property(properties, 'Booking phone', textValue(form, '[data-sixtea-phone]'));
    property(properties, 'Preferred event date', textValue(form, '[data-sixtea-event-date]'));
    property(properties, 'Alternate date', textValue(form, '[data-sixtea-alternate-date]'));
    property(properties, 'Guest count', guestCount);
    property(properties, 'Venue or city', textValue(form, '[data-sixtea-venue]'));
    property(properties, 'Occasion', textValue(form, '[data-sixtea-occasion]'));
    property(properties, 'Booking notes', textValue(form, '[data-sixtea-notes]'));

    const quantity = experience.quantityMode === 'guest_count' ? guestCount : 1;

    if (submit) {
      submit.disabled = true;
      submit.dataset.originalText = submit.textContent;
      submit.textContent = 'Preparing checkout…';
    }
    setStatus(form, 'Adding your selected experience to Shopify checkout…');

    try {
      if (form.dataset.clearCart === 'true') {
        const clearResponse = await fetch(CLEAR_URL, {
          method: 'POST',
          headers: { Accept: 'application/json' }
        });
        if (!clearResponse.ok) throw new Error('The cart could not be prepared.');
      }

      const response = await fetch(ADD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: Number(variant.id), quantity, properties }]
        })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result.description || result.message || 'This experience could not be added to the cart.'
        );
      }

      window.location.assign(CHECKOUT_URL);
    } catch (error) {
      setStatus(
        form,
        error.message || 'Checkout could not be started. Please try again.',
        'error'
      );
      if (submit) {
        submit.disabled = false;
        submit.textContent = submit.dataset.originalText || 'Continue to Shopify checkout';
      }
    }
  };

  const submitInquiry = (form) => {
    if (!form.reportValidity()) return;

    const proxy = document.querySelector('[data-sixtea-inquiry-proxy]');
    if (!proxy) {
      setStatus(form, 'The inquiry form is unavailable. Please use the contact page.', 'error');
      return;
    }

    const experience = form.__sixteaExperience;
    const variantSelect = form.querySelector('[data-sixtea-variant-select]');
    const selectedVariant = experience && (experience.variants || []).find(
      (item) => String(item.id) === String(variantSelect && variantSelect.value)
    );

    const mapping = {
      'contact[name]': textValue(form, '[data-sixtea-name]'),
      'contact[email]': textValue(form, '[data-sixtea-email]'),
      'contact[phone]': textValue(form, '[data-sixtea-phone]'),
      'contact[experience]': experience ? experience.name : '',
      'contact[package]': selectedVariant && !selectedVariant.isDefault ? selectedVariant.title : '',
      'contact[event_date]': textValue(form, '[data-sixtea-event-date]'),
      'contact[alternate_date]': textValue(form, '[data-sixtea-alternate-date]'),
      'contact[guest_count]': textValue(form, '[data-sixtea-guest-count]'),
      'contact[venue_or_city]': textValue(form, '[data-sixtea-venue]'),
      'contact[occasion]': textValue(form, '[data-sixtea-occasion]'),
      'contact[body]': textValue(form, '[data-sixtea-notes]')
    };

    Object.entries(mapping).forEach(([name, value]) => {
      const input = proxy.querySelector(`[name="${name}"]`);
      if (input) input.value = value;
    });

    proxy.requestSubmit();
  };

  const initializeForms = () => {
    const { ordered, map } = parseConfigs();
    if (!ordered.length) return { ordered, map };

    const params = new URLSearchParams(window.location.search);
    const requestedKey = normalizeKey(params.get('experience'));

    document.querySelectorAll('[data-sixtea-shopify-booking-form]').forEach((form) => {
      const select = form.querySelector('[data-sixtea-experience-select]');
      const defaultKey = requestedKey || normalizeKey(form.dataset.defaultExperience) || normalizeKey(select && select.value);

      populateExperienceSelect(select, ordered, defaultKey);

      if (form.dataset.sixteaInitialized !== 'true') {
        form.dataset.sixteaInitialized = 'true';

        if (select) {
          select.addEventListener('change', () => chooseExperience(form, select.value, parseConfigs().map));
        }

        const variantSelect = form.querySelector('[data-sixtea-variant-select]');
        if (variantSelect) variantSelect.addEventListener('change', () => updatePrice(form));

        const guestInput = form.querySelector('[data-sixtea-guest-count]');
        if (guestInput) guestInput.addEventListener('input', () => updatePrice(form));

        form.addEventListener('submit', (event) => {
          event.preventDefault();
          submitCheckout(form);
        });

        const inquiryButton = form.querySelector('[data-sixtea-inquiry-submit]');
        if (inquiryButton) inquiryButton.addEventListener('click', () => submitInquiry(form));
      }

      chooseExperience(form, defaultKey, map);
    });

    return { ordered, map };
  };

  const selectFromButton = (button) => {
    const key = normalizeKey(button.dataset.experienceKey);
    const form = document.querySelector('#shopify-booking [data-sixtea-shopify-booking-form], [data-sixtea-shopify-booking-form]');
    const { map } = initializeForms();

    if (form && key) {
      chooseExperience(form, key, map);
    }

    const target = document.getElementById('shopify-booking') || form;
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (key && map.has(key)) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('experience');
      cleanUrl.hash = 'shopify-booking';
      history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-sixtea-select-experience]');
    if (!button) return;
    event.preventDefault();
    selectFromButton(button);
  });

  const init = () => {
    const { map } = initializeForms();
    const params = new URLSearchParams(window.location.search);
    const requestedKey = normalizeKey(params.get('experience'));

    if (requestedKey && map.has(requestedKey) && window.location.hash === '#shopify-booking') {
      const target = document.getElementById('shopify-booking');
      if (target) requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
